import React from "react";
import styled from "styled-components/macro";
import Close from "@material-ui/icons/Close";
import { theme, getStatusMeta } from "./theme";

function formatDateTime(unix) {
  const date = unix instanceof Date ? unix : new Date(unix || Date.now());
  const pad = (n) => `0${n}`.slice(-2);
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

const JobDetailDrawer = ({ job, printers, onClose, onCancel }) => {
  if (!job) return null;

  const meta = getStatusMeta(job.status);
  const printer = printers?.[job.printer_id];
  const isCancelable = ["wait", "printing"].includes(job.status);
  const reasonText = job.cancelReasonTitle || job.failReasonTitle;

  const activityLines = [`Submitted at ${formatDateTime(job.unix)}`, `Current status: ${meta.label}`];
  if (reasonText) activityLines.push(`Reason: ${reasonText}`);
  // Only meaningful once the job is actually canceled — cancelByUser/canceledByName
  // are otherwise just their unset defaults on any non-canceled job.
  if (job.status === "Cancel") {
    activityLines.push(
      job.cancelByUser ? "Canceled by the user" : `Canceled by CS: ${job.canceledByName || "—"}`
    );
  }

  return (
    <>
      <Backdrop onClick={onClose} />
      <Drawer>
        <Header>
          <Title>{job.title}</Title>
          <CloseBtn onClick={onClose} aria-label="Close">
            <Close style={{ fontSize: 18 }} />
          </CloseBtn>
        </Header>

        <StatusBadge badgeColor={meta.color} badgeBackground={meta.background}>
          {meta.label}
        </StatusBadge>

        <Section>
          <Row>
            <RowLabel>User</RowLabel>
            <RowValue>{job.userEmail || job.userName || "—"}</RowValue>
          </Row>
          <Row>
            <RowLabel>Printer / unit</RowLabel>
            <RowValue>{job.roomName || printer?.room_name || "—"}</RowValue>
          </Row>
          <Row>
            <RowLabel>Submitted</RowLabel>
            <RowValue>{formatDateTime(job.unix)}</RowValue>
          </Row>
          <Row>
            <RowLabel>Pages</RowLabel>
            <RowValue>{job.pages ?? "—"}</RowValue>
          </Row>
          <Row>
            <RowLabel>Copies</RowLabel>
            <RowValue>{job.copies ?? "—"}</RowValue>
          </Row>
          <Row>
            <RowLabel>Color</RowLabel>
            <RowValue>{job.color ? "Color" : "Black & white"}</RowValue>
          </Row>
          <Row>
            <RowLabel>Duplex</RowLabel>
            <RowValue>{job.duplex ? "Double-sided" : "Single-sided"}</RowValue>
          </Row>
        </Section>

        <SectionTitle>Activity</SectionTitle>
        <ActivityList>
          {activityLines.map((line, index) => (
            <ActivityItem key={index}>{line}</ActivityItem>
          ))}
        </ActivityList>

        <Footer>
          {isCancelable ? (
            <CancelBtn onClick={() => onCancel(job)}>Cancel job</CancelBtn>
          ) : null}
          <CloseFooterBtn onClick={onClose}>Close</CloseFooterBtn>
        </Footer>
      </Drawer>
    </>
  );
};

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.15);
  z-index: 30;
  animation: drawerBackdropFadeIn 200ms ease;

  @keyframes drawerBackdropFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 360px;
  max-width: 90vw;
  background: ${theme.surface};
  box-shadow: ${theme.shadowHover};
  z-index: 31;
  display: flex;
  flex-direction: column;
  padding: 24px;
  font-family: ${theme.fontFamily};
  overflow-y: auto;
  animation: drawerSlideIn 220ms cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes drawerSlideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.text};
  word-break: break-word;
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  outline: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  color: ${theme.textFaint};

  &:hover {
    background: ${theme.background};
    color: ${theme.text};
  }
`;

const StatusBadge = styled.div`
  align-self: flex-start;
  color: ${({ badgeColor }) => badgeColor};
  background: ${({ badgeBackground }) => badgeBackground};
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: ${theme.radiusPill};
  margin-bottom: 20px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${theme.border};
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const RowLabel = styled.div`
  font-size: 12px;
  color: ${theme.textFaint};
  flex-shrink: 0;
`;

const RowValue = styled.div`
  font-size: 13px;
  color: ${theme.text};
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.textFaint};
  margin-bottom: 8px;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ActivityItem = styled.div`
  font-size: 13px;
  color: ${theme.textMuted};
  padding-left: 12px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 6px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${theme.primary};
  }
`;

const Footer = styled.div`
  margin-top: auto;
  display: flex;
  gap: 10px;
  padding-top: 20px;
`;

const CancelBtn = styled.button`
  flex: 1;
  outline: none;
  border: none;
  cursor: pointer;
  font-family: ${theme.fontFamily};
  font-size: 13px;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: ${theme.radiusSm};
  color: #ffffff;
  background: ${theme.danger};

  &:hover {
    opacity: 0.9;
  }
`;

const CloseFooterBtn = styled.button`
  flex: 1;
  outline: none;
  border: 1px solid ${theme.border};
  cursor: pointer;
  font-family: ${theme.fontFamily};
  font-size: 13px;
  font-weight: 500;
  padding: 10px 16px;
  border-radius: ${theme.radiusSm};
  color: ${theme.textMuted};
  background: ${theme.surface};

  &:hover {
    background: ${theme.background};
  }
`;

export { JobDetailDrawer };
